// src/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import api from './axios'; // je axios instance met withCredentials: true

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthContextProvider = ({ children }) => {
  const [user, setUser]               = useState(null);
  const [companyId, setCompanyId]     = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [loading, setLoading]         = useState(true);

  // Helper om alleen bepaalde velden van user te updaten
  const updateUser = (updatedFields) => {
    setUser(prev => prev ? { ...prev, ...updatedFields } : prev);
  };

  // 1) Bij mount: check sessie via /auth/me
  useEffect(() => {
    api.get('/auth/me')
      .then(res => {
        const u = res.data.user;
        setUser(u);

        setCompanyId(u.default_companyid);
        setCompanyName(u.default_companyname);
      })
      .catch(() => {
        setUser(null);
        setCompanyId(null);
        setCompanyName(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // 2) Axios interceptor voor automatisch token-refresh
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      resp => resp,
      async err => {
        const orig = err.config;
        // Sla login en refresh calls over, en voorkom infinite loop
        if (
          orig._retry ||
          orig.url.endsWith('/login') ||
          orig.url.endsWith('/auth/refresh')
        ) {
          return Promise.reject(err);
        }
        if (err.response?.status === 401) {
          orig._retry = true;
          try {
            await api.post('/auth/refresh');
            return api(orig);
          } catch (refreshErr) {
            // Refresh mislukt: log uit
            setUser(null);
            setCompanyId(null);
            setCompanyName(null);
            return Promise.reject(refreshErr);
          }
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(interceptor);
  }, []);

  // 3) Login: stuur credentials, cookies worden gezet, context vullen
  const login = async (email, password, remember) => {
    const res = await api.post('/login', { email, password, keepMeSignedIn: remember });
    const u = res.data.user || {};
    if (res.data.mfa_enabled !== undefined) {
      u.mfa_enabled = res.data.mfa_enabled;
    }
    setUser(u);
    setCompanyId(u.default_companyid);
    setCompanyName(u.default_companyname);
    return u;
  };

  // 4) Switch Company
  const switchCompany = (id, name) => {
    setCompanyId(id);
    setCompanyName(name);
  };

  // 5) Logout: server-side cookies wissen, state clearen
  const logout = async () => {
    await api.post('/auth/logout');
    setUser(null);
    setCompanyId(null);
    setCompanyName(null);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{
      user,
      companyId,
      companyName,
      login,
      updateUser,
      switchCompany,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};
